import appInfo from "@common/appInfo";
import { type InputTypeProps, type WithRef } from "@common/declarations";
import { MAX_DB_INT } from "@common/lib/misc";
import { type CurrencyOptionItem } from "@common/lib/options";
import * as React from "react";
import { Select } from "../Select/component";
import { type TextInputProps } from "../Text";
import NumberInput, { type NumberInputConfigProps, type NumberInputProps } from "./component";

export type MoneyInputProps = NumberInputProps & Partial<WithRef<any>>;

const MoneyInput = (props: MoneyInputProps) => (
  <NumberInput
    {...props}
    addon={props.addon ?? appInfo.currencyCode}
    fractionalDigits={props.fractionalDigits ?? 2}
    significantDigits={props.significantDigits ?? 2}
    min={props.min ?? 0}
    max={props.max ?? MAX_DB_INT}
  />
);

export default MoneyInput;

export type Currency = {
  currencyCode: string;
  isCommission: boolean;
  label?: string;
};

export type MoneyWithCurrencyValue = {
  amount?: number | null;
  currency?: string;
};

export type MoneyWithCurrencyInputProps = Omit<TextInputProps, "onChange" | "value"> &
  InputTypeProps<MoneyWithCurrencyValue | null | undefined> &
  NumberInputConfigProps & {
    selectedLabel?: string;
    options: CurrencyOptionItem[];
    currencyCode: string;
    currencies: Currency[];
  };

export const MoneyWithCurrencyInput = ({ onChange, value, ...props }: MoneyWithCurrencyInputProps) => {
  const _onChange = React.useCallback(
    (v: number) => {
      onChange({
        ...(value ?? {
          currency: props.currencyCode
        }),
        amount: v
      });
    },
    [props.currencyCode, onChange, value]
  );

  const onCurrencyChange = React.useCallback(
    (v: string) => {
      onChange({
        ...(value ?? {}),
        currency: v
      });
    },
    [onChange, value]
  );

  const addon = React.useMemo(() => {
    const currency = value?.currency ?? props.currencyCode;
    if (props.currencies?.length === 1) return currency;

    return (
      <Select
        onChange={onCurrencyChange}
        options={props.options}
        isRequired
        isDisabled={props.isDisabled || props.isReadonly}
        name={props.name}
        selectedLabel={props.selectedLabel ?? value?.currency}
        value={currency}
      />
    );
  }, [
    props.currencyCode,
    props.currencies,
    onCurrencyChange,
    props.isDisabled,
    props.isReadonly,
    props.placeholder,
    value
  ]);

  return <MoneyInput {...props} addon={addon} onChange={_onChange} value={value?.amount} />;
};
